import { useMemo, useState } from 'react';

import { AssetContractSignModal } from '@ping/components/AssetContractSignModal';
import { GetStartedButton } from '@ping/components/Header';
import { parsePair, t } from '@ping/helpers';
import { useAssetContractStatus, useIsUserLoggedIn } from '@ping/hooks';
import { useDesktopDevice, useMobile, useTabletDevice } from '@ping/hooks/useBreakpoint';
import { selectedMarketInstrumentSelector, useMarketStore } from '@ping/stores/market.store';
import { isUserActivityStatusBannedSelector, useUserInformationStore } from '@ping/stores/userInformation.store';
import { WEBSITE_BASE_URL } from '@ping/configs';
import { Button } from '@ping/uikit';
import LockIcon from '@ping/assets/Icon/lock.svg';
import { OrderForm } from './OrderForm';
import { OrderFormModalTriggers } from './OrderFormModalTriggers';

import style from './style.module.scss';

const SignInOverlay = () => (
  <div className={style['overlay']} data-testid='sign-in-overlay'>
    <div className={style['overlay__title']}>{t('Start trading today')}</div>
    <p className={style['overlay__description']}>
      {t('By creating an account you agree to our ', 'Data protection guidelines')}
      &nbsp;
      <a href={`${WEBSITE_BASE_URL}terms-of-service`} target='_blank' className={style['overlay__link']}>
        {t('Terms and Conditions')}
      </a>
      &nbsp;
      {t('and')}
      &nbsp;
      <a href={`${WEBSITE_BASE_URL}privacy-policy`} target='_blank' className={style['overlay__link']}>
        {t('Data protection guidelines')}
      </a>
    </p>
    <GetStartedButton />
  </div>
);

const BannedOverlay = () => (
  <div className={style['overlay']} data-testid='banned-in-overlay'>
    <p className={style['overlay__description']}>{t('Sorry we cannot provide a service to you at this moment.')}</p>
  </div>
);

export const MarketOrderForm = () => {
  const instrument = useMarketStore(selectedMarketInstrumentSelector);
  const isUserLoggedIn = useIsUserLoggedIn();
  const isUserActivityStatusBanned = useUserInformationStore(isUserActivityStatusBannedSelector);
  const pair = useMemo(() => parsePair.byInstrument(instrument), [instrument]);
  const isDesktop = useDesktopDevice();
  const isTablet = useTabletDevice();
  const isMobile = useMobile();

  const { isContractSigned, getAssetContract } = useAssetContractStatus();
  const baseAsset = pair.base?.toUpperCase() || '';
  const contractUnsigned = isUserLoggedIn && !!baseAsset && !isContractSigned(baseAsset);
  const [contractModalOpen, setContractModalOpen] = useState(false);

  const handleSignContract = () => {
    setContractModalOpen(true);
  };

  const assetContract = contractUnsigned ? getAssetContract(baseAsset) : undefined;

  return (
    <div className={style['market-order-form']}>
      {!isUserLoggedIn && isDesktop && <SignInOverlay />}
      {isUserActivityStatusBanned && <BannedOverlay />}
      {contractUnsigned && (
        <>
          <div
            className={`${style['overlay']} ${style['overlay--always-visible']}`}
            data-testid='contract-sign-overlay'
          >
            <div className={style['overlay__content']}>
              <LockIcon className={style['overlay__lock-icon']} />
              <div className={style['overlay__title']}>{t('Contract Signing Required')}</div>
              <p className={style['overlay__description']}>
                {t('You need to sign a contract before trading %s.', baseAsset)}
              </p>
              <Button uppercase onClick={handleSignContract}>
                {t('Sign Contract')}
              </Button>
            </div>
          </div>
          <AssetContractSignModal
            isOpen={contractModalOpen}
            onClose={() => setContractModalOpen(false)}
            assetId={assetContract?.assetId || baseAsset}
            assetName={assetContract?.assetName || baseAsset}
            onSigned={() => setContractModalOpen(false)}
          />
        </>
      )}
      {isDesktop && <OrderForm pair={pair} />}
      {isTablet &&
        (contractUnsigned ? (
          <div className={style['contract-sign-triggers']}>
            <Button
              className={style['contract-sign-triggers__button']}
              size='large'
              uppercase
              onClick={handleSignContract}
            >
              {t('Sign contract to trade')}
            </Button>
          </div>
        ) : (
          <OrderFormModalTriggers pair={pair} entrance='start' />
        ))}
      {isMobile &&
        (contractUnsigned ? (
          <div className={style['contract-sign-triggers']}>
            <Button
              className={style['contract-sign-triggers__button']}
              size='large'
              uppercase
              onClick={handleSignContract}
            >
              {t('Sign contract to trade')}
            </Button>
          </div>
        ) : (
          <OrderFormModalTriggers pair={pair} />
        ))}
    </div>
  );
};
